// core/stores/bookmarkStore.ts — Zustand store for bookmarks + folders.
//
// Hydrated at module init from the typed seed. Services mutate this store;
// hooks read it reactively. Components must use hooks, not this store.

import { create } from 'zustand';
import type { Bookmark, BookmarkFolder } from '@/core/models/bookmark';
import { seedBookmarks, seedBookmarkFolders } from '@/core/data/bookmarks';
import { bootSeed } from './bootSeed';

interface BookmarkState {
  bookmarks: Bookmark[];
  folders: BookmarkFolder[];

  // Replace the entire bookmarks list (used by _resetForTest).
  setBookmarks: (bookmarks: Bookmark[]) => void;

  // Replace the entire folders list.
  setFolders: (folders: BookmarkFolder[]) => void;

  // Remove a bookmark by id; no-op if not found.
  removeBookmark: (id: string) => void;

  // Prepend a bookmark; no-op if one with the same postId already exists.
  addBookmark: (bookmark: Bookmark) => void;

  // Append a new folder with the given name; returns the generated id.
  addFolder: (name: string) => string;

  // Rename an existing folder by id; no-op if not found.
  renameFolder: (id: string, name: string) => void;

  // Remove a folder; orphaned bookmarks are reassigned to an on-demand
  // "unsorted" folder. No-op if id === 'unsorted'.
  removeFolder: (id: string) => void;

  // Move a bookmark to a different folder.
  setBookmarkFolder: (bookmarkId: string, folderId: string) => void;
}

export const useBookmarkStore = create<BookmarkState>()((set) => ({
  // Deep-clone the seed so test resets don't mutate the imported const.
  // Empty in the running app (see bootSeed.ts); the seed only exists under the test runner.
  bookmarks: bootSeed(seedBookmarks).map((b) => ({ ...b })),
  // Folders are static (no mutations needed), but included for reactive reads.
  folders: bootSeed(seedBookmarkFolders).map((f) => ({ ...f })),

  setBookmarks: (bookmarks) => set({ bookmarks }),

  setFolders: (folders) => set({ folders }),

  removeBookmark: (id) =>
    set((s) => ({
      bookmarks: s.bookmarks.filter((b) => b.id !== id),
    })),

  addBookmark: (bookmark) =>
    set((s) =>
      bookmark.postId !== null &&
      s.bookmarks.some(
        (b) =>
          b.postId === bookmark.postId && (b.commentId ?? null) === (bookmark.commentId ?? null),
      )
        ? s
        : { bookmarks: [bookmark, ...s.bookmarks] },
    ),

  addFolder: (name) => {
    const id = `folder-${crypto.randomUUID()}`;
    set((s) => ({ folders: [...s.folders, { id, name }] }));
    return id;
  },

  renameFolder: (id, name) =>
    set((s) => ({
      folders: s.folders.map((f) => (f.id === id ? { ...f, name } : f)),
    })),

  removeFolder: (id) =>
    set((s) => {
      if (id === 'unsorted') return s;
      const hasUnsorted = s.folders.some((f) => f.id === 'unsorted');
      const folders = [
        ...s.folders.filter((f) => f.id !== id),
        ...(hasUnsorted ? [] : [{ id: 'unsorted', name: 'Unsorted' }]),
      ];
      const bookmarks = s.bookmarks.map((b) =>
        b.folderId === id ? { ...b, folderId: 'unsorted' } : b,
      );
      return { folders, bookmarks };
    }),

  setBookmarkFolder: (bookmarkId, folderId) =>
    set((s) => ({
      bookmarks: s.bookmarks.map((b) => (b.id === bookmarkId ? { ...b, folderId } : b)),
    })),
}));
